import { createReducer, on } from '@ngrx/store';

import { BookingLocationCodes } from '@fcom/common/interfaces/booking';
import { DeepLinkSource } from '@fcom/common';
import { BookingRef } from '@fcom/one-upgrade/interfaces';

import { PaymentTotal } from '../../interfaces/payment-total.interface';
import { Buyer, FlightsV2, FlightV2, Passengers, UpgradeResponse, VoucherType } from '../../api/models';
import { getVoucherAmount, calculateMinimumPayment } from '../../utils/payment.utils';
import { DataState } from '../../interfaces/store.interface';
import { AuthActions, DataActions } from '../actions';

export const initialState: DataState = {
  user: null,
  booking: null,
  summary: [],
  flights: [],
  passengers: [],
  source: null,
  upgradeResponse: null,
};

const dataReducer = createReducer(
  initialState,
  on(AuthActions.logout, () => initialState),
  on(AuthActions.setBookingRef, (state, { lastName, recLoc }) => ({
    ...state,
    booking: {
      lastName: lastName,
      recLoc: recLoc,
    },
  })),
  on(AuthActions.setUpgradeData, (state, { booking, buyer }) => ({
    ...state,
    flights: booking.flights,
    passengers: booking.passengers,
    user: buyer,
  })),
  on(AuthActions.flightSummary, (state, { summary }) => ({ ...state, summary: summary.slice() })),
  on(DataActions.setUpgradeResponse, (state, { upgradeResponse }) => ({ ...state, upgradeResponse })),
  on(DataActions.setDeepLinkSource, (state, { source }) => ({ ...state, source })),
  on(DataActions.clearDeepLinkSource, (state) => ({ ...state, source: null })),
  on(DataActions.clearFlights, (state) => ({ ...state, flights: [] })),
  on(DataActions.clearPassengers, (state) => ({ ...state, passengers: [] })),
  on(DataActions.clearAll, (state) => ({
    ...state,
    user: null,
    flights: [],
    passengers: [],
    summary: [],
    source: null,
  }))
);

export function reducer(
  state: DataState,
  action: AuthActions.AuthActionsUnion | DataActions.DataActionsUnion
): DataState {
  return dataReducer(state, action);
}

export const getUser = (state: DataState): Buyer => state.user || {};
export const getUserPoints = (state: DataState): number => getUser(state).points || 0;
export const getUserShortHaul = (state: DataState): number =>
  getVoucherAmount(VoucherType.EuropeUpgrade, getUser(state));
export const getUserLongHaul = (state: DataState): number =>
  getVoucherAmount(VoucherType.LongHaulUpgrade, getUser(state));
export const getUserUpgradeBenefit = (state: DataState): number =>
  getVoucherAmount(VoucherType.UpgradeBenefit, getUser(state));
export const getPassengers = (state: DataState): Passengers => {
  return state.passengers.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
};
export const getFlights = (state: DataState): FlightsV2 => state.flights;
export const getBookingRef = (state: DataState): BookingRef => state.booking;
export const getJourneySummary = (state: DataState): BookingLocationCodes[] => state.summary;
export const getDeepLinkSource = (state: DataState): DeepLinkSource => state.source;
export const getMinimumPayment = (state: DataState): PaymentTotal => {
  return calculateMinimumPayment(
    getFlights(state)
      .map((flight: FlightV2) => flight.upgrades)
      .flat()
      .map((upgrade) => upgrade.paymentOptions)
      .filter(Boolean)
  );
};

export const getUpgradeResponse = (state: DataState): UpgradeResponse => state.upgradeResponse;
